
import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";

interface RelatedArticle {
  id: string;
  slug: string;
  title: string;
}

interface ArticleSidebarProps {
  relatedArticles: RelatedArticle[] | undefined;
  isHeyHumansArticle?: boolean;
}

const ArticleSidebar = ({ relatedArticles, isHeyHumansArticle = false }: ArticleSidebarProps) => {
  return (
    <div className="lg:col-span-4">
      <div className="sticky top-24 space-y-16">
        {/* Hey Humans specific CTA for their article */}
        {isHeyHumansArticle ? (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl shadow-sm border border-green-200">
            <div className="text-center mb-4">
              <img 
                src="/lovable-uploads/e3ece06c-b176-4067-a58f-bb4f1c09d65a.png" 
                alt="Hey Humans Natural Deodorant" 
                className="w-24 h-24 object-cover mx-auto mb-4 rounded-lg"
              />
              <h4 className="text-lg font-semibold text-green-800">Try Hey Humans Today!</h4>
              <p className="text-sm text-green-700 mt-2 mb-4">
                Aluminum-free, plastic-free deodorant that actually works for all-day protection!
              </p>
              <Button asChild className="w-full bg-green-600 hover:bg-green-700 pulse-on-hover">
                <a 
                  href="https://www.heyhumansusa.com/?dt_id=2391110" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Shop Hey Humans Now
                </a>
              </Button>
            </div>
          </div>
        ) : (
          /* Default enhanced sidebar CTA */
          <div className="bg-primary/10 p-6 rounded-xl shadow-sm border border-primary/20">
            <div className="text-center mb-4">
              <img 
                src="/lovable-uploads/a38e4978-a37a-452c-a67e-140d9acc3d2e.png" 
                alt="It Just Works Deodorant" 
                className="w-24 h-24 object-cover mx-auto mb-4"
              />
              <h4 className="text-lg font-semibold">Tired of Ineffective Deodorants?</h4>
              <p className="text-sm text-muted-foreground mt-2 mb-4">
                Try our natural formula that actually works for sensitive skin!
              </p>
              <Button asChild className="w-full pulse-on-hover">
                <a 
                  href="https://itjustworksdeodorant.com?sca_ref=8468108.vEEWUNzhD1" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Shop Now - 15% OFF
                </a>
              </Button>
            </div>
          </div>
        )}

        {relatedArticles && relatedArticles.length > 0 && (
          <div className="bg-muted p-10 rounded-xl shadow-sm">
            <h4 className="text-lg font-medium mb-8">Related Articles</h4>
            <div className="space-y-8">
              {relatedArticles.map((related) => (
                <div key={related.id} className="border-b border-border/50 pb-8 last:border-0 last:pb-0">
                  <Link to={`/article/${related.slug}`} className="text-base font-medium hover:text-primary transition-colors block">
                    {related.title}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-primary/5 p-10 rounded-xl shadow-sm">
          <h4 className="text-lg font-medium mb-8">Our Recommended Products</h4>
          <div className="space-y-8">
            {/* Show Hey Humans first if it's the Hey Humans article */}
            {isHeyHumansArticle ? (
              <>
                <a 
                  href="https://www.heyhumansusa.com/?dt_id=2391110" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block p-8 border border-green-200 rounded-xl bg-green-50 hover:bg-green-100 transition-colors"
                >
                  <h4 className="font-medium text-green-800">Hey Humans Natural Deodorant</h4>
                  <p className="text-sm text-green-700 mt-3">Aluminum-free, plastic-free deodorant that works!</p>
                </a>
                <a 
                  href="https://www.skillano-beauty.com/?ref=sfvnconi" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block p-8 border border-border/50 rounded-xl bg-background hover:bg-accent/50 transition-colors"
                >
                  <h4 className="font-medium">Skillano Beauty</h4>
                  <p className="text-sm text-muted-foreground mt-3">Premium skincare for your underarm health</p>
                </a>
              </>
            ) : (
              <>
                <a 
                  href="https://itjustworksdeodorant.com?sca_ref=8468108.vEEWUNzhD1" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block p-8 border border-border/50 rounded-xl bg-background hover:bg-accent/50 transition-colors"
                >
                  <h4 className="font-medium">It Just Works Deodorant</h4>
                  <p className="text-sm text-muted-foreground mt-3">Natural deodorant that actually works!</p>
                </a>
                <a 
                  href="https://www.skillano-beauty.com/?ref=sfvnconi" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block p-8 border border-border/50 rounded-xl bg-background hover:bg-accent/50 transition-colors"
                >
                  <h4 className="font-medium">Skillano Beauty</h4>
                  <p className="text-sm text-muted-foreground mt-3">Premium skincare for your underarm health</p>
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleSidebar;
